import {
  BehaviorSubject,
  ReplaySubject,
  distinctUntilChanged,
  firstValueFrom,
  map,
  merge,
  startWith,
  tap,
} from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  Input,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormProperty } from 'src/app/core/models/form.models';
import {
  NomLanguages,
  NomMediaChannel,
  NomObjectTypes,
  WidgetType,
} from 'src/app/core/constants';

import { AppState } from 'src/app/core/state/app.state';
import { FeedSettingsService } from 'src/app/core/services/feed-settings.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { NomChartType } from 'src/app/core/models/widget.models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSettingsComponent } from '../widget-settings/widget-settings.component';
import { WidgetSettingsService } from 'src/app/widgets/services/widget.settings.service';
import { selectUser } from 'src/app/core/state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-widget-settings-general',
  templateUrl: './widget-settings-general.component.html',
  styleUrls: ['./widget-settings-general.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class WidgetSettingsGeneralComponent extends WidgetSettingsComponent {
  @Input() tab: string;

  formTabDataSources: any;

  summaryFilters: any[] = [];

  private summaryFilterOptionsSubject = new BehaviorSubject<any[]>([]);

  // emit new options based on media channel type
  private filterOptionsSubject = new ReplaySubject<any[]>(
    NomMediaChannel.ONLINE,
  );

  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
    protected feedSettings: FeedSettingsService,
  ) {
    super(formService, widgetSettings, cdf, store, logger);
  }

  ngOnInit(): void {
    // ! !!! here again the order is important - NOT GOOD AT ALL!!!
    this.initializeFormTabDataSources();
    this.initializeFormProperties();
    super.ngOnInit();
    if (this.widget && !this.massUpdate) {
      this.initializeColorsControl();
      this.applyDatasourceFilter();
    }
    this.initializeForm();

    // custom logic for media channel type changes to update sort by metric options
    const mediaChannelField =
      'settings.feed.content_filters.filters.post_source_media_channel_id.include';

    this.formGroup.addControl(mediaChannelField, new FormArray([]));

    const mediaChannelControl = this.formGroup.get([
      'settings.feed.media_channel_id',
    ]);

    if (mediaChannelControl) {
      mediaChannelControl.valueChanges
        .pipe(
          startWith(mediaChannelControl.value),
          distinctUntilChanged(), // Prevent unnecessary updates
        )
        .subscribe((mediaChannelValue) => {
          this.logger.debug(
            `Media Channel Type changed to ${mediaChannelValue} in Widget Settings General`,
          );
          const newOptions = this.getSortOptionsFor(mediaChannelValue);
          this.filterOptionsSubject.next(newOptions);

          const filterControl = this.formGroup.get([
            mediaChannelField,
          ]) as FormArray;
          filterControl?.clear();
          filterControl?.push(new FormControl(mediaChannelValue));
        });
      const currentValue = mediaChannelControl.value;
      mediaChannelControl.patchValue(currentValue, { emitEvent: true });
    }

    // Update Datasource, Filter and Pivot tab counters on change
    const streams = [
      this.formGroup
        .get(['settings.dimension_id_filter'])
        ?.valueChanges.pipe(map((value) => ({ tabName: 'filters', value }))),
      this.formGroup
        .get(['settings.dimension_id_pivot'])
        ?.valueChanges.pipe(map((value) => ({ tabName: 'pivots', value }))),
      this.formGroup
        .get(['settings.datasource_object_id'])
        ?.valueChanges.pipe(
          map((value) => ({ tabName: 'datasources', value })),
        ),
    ].filter((stream) => !!stream); // Remove undefined/null entries

    if (streams.length > 0) {
      merge(...streams)
        .pipe(
          tap(({ tabName, value }) => {
            const index = this.formTabDataSources?.items.findIndex(
              (tab) => tab.name === tabName,
            );
            if (index !== -1 && index !== undefined) {
              this.formTabDataSources.items[index] = {
                ...this.formTabDataSources.items[index],
                counter: value?.length ?? 0,
              };
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  async initializeForm() {
    const user = await firstValueFrom(this.store.select(selectUser));

    if (this.widget) {
      // Single widget logic
      const objectTypeId =
        this.widget.datasources?.[0]?.object_type_id ??
        NomObjectTypes.ONLINE_FEED;

      this.summaryFilters = await this.feedSettings.getSummaryFilterOptions(
        [objectTypeId],
        user,
      );
      this.summaryFilterOptionsSubject.next(this.summaryFilters);
    }

    if (this.massUpdate && this.widgets?.length) {
      // Extract unique objectTypeIds from all widgets
      const objectTypeIds = Array.from(
        new Set(
          this.widgets
            .map((w) => w?.datasources?.[0]?.object_type_id)
            .filter((id): id is NomObjectTypes => id !== undefined),
        ),
      );

      // Fallback to default if no valid IDs found
      const finalIds =
        objectTypeIds.length > 0 ? objectTypeIds : [NomObjectTypes.ONLINE_FEED];

      this.summaryFilters = await this.feedSettings.getSummaryFilterOptions(
        finalIds,
        user,
      );
      this.summaryFilterOptionsSubject.next(this.summaryFilters);
    }
  }

  getSortOptionsFor(mediaTypeId: number): any[] {
    //Global Base Options
    const baseOptions = [
      {
        value: 'post_time_published',
        name: this.translateService.instant('forms.form-option.time-published'),
      },
      {
        value: 'post_reach',
        name: this.translateService.instant(
          'forms.form-option.potential-reach',
        ),
      },
      {
        value: 'post_reach_v2',
        name: this.translateService.instant(
          'forms.form-option.estimated-reach',
        ),
      },
    ];

    // Social-specific
    const socialOptions = [
      {
        value: 'post_social_followers',
        name: this.translateService.instant('forms.form-option.followers'),
      },
      {
        value: 'post_social_likes_count',
        name: this.translateService.instant('forms.form-option.likes'),
      },
      {
        value: 'post_social_dislikes_count',
        name: this.translateService.instant('forms.form-option.dislikes'),
      },
      {
        value: 'post_social_comments_count',
        name: this.translateService.instant('forms.form-option.comments'),
      },
      {
        value: 'post_social_shares_count',
        name: this.translateService.instant('forms.form-option.shares'),
      },
      {
        value: 'post_social_reactions_count',
        name: this.translateService.instant('forms.form-option.reactions'),
      },
      {
        value: 'post_social_interactions_count',
        name: this.translateService.instant('forms.form-option.interactions'),
      },
      {
        value: 'post_social_engagements_count',
        name: this.translateService.instant('forms.form-option.engagements'),
      },
    ];

    // Online options
    const onlineOptions = [
      {
        value: 'post_source_visits_per_day',
        name: this.translateService.instant(
          'forms.form-option.source-daily-visits',
        ),
      },
      {
        value: 'source_rank_global',
        name: this.translateService.instant(
          'forms.form-option.source-global-rank',
        ),
      },
      {
        value: 'source_rank_country',
        name: this.translateService.instant(
          'forms.form-option.source-domestic-rank',
        ),
      },
    ];

    switch (mediaTypeId) {
      case NomMediaChannel.ONLINE:
        return [...baseOptions, ...onlineOptions];

      case NomMediaChannel.SOCIAL:
        return [...baseOptions, ...socialOptions];

      case NomMediaChannel.PRINT:
      case NomMediaChannel.BROADCAST:
      default:
        return [...baseOptions];
    }
  }

  private initializeFormTabDataSources(): void {
    this.formTabDataSources = {
      tabIndex: 0,
      tab: this.tab ?? '',
      items: [
        {
          name: 'datasources',
          label: this.translateService.instant(
            'widget.settings-general.tab.datasources',
          ),
          counter: this.widget?.datasources?.length ?? 0,
        },
        {
          name: 'filters',
          label: this.translateService.instant(
            'widget.settings-general.tab.filters',
          ),
          counter: this.widget?.dimensions_filter?.length ?? 0,
        },
        {
          name: 'pivots',
          label: this.translateService.instant(
            'widget.settings-general.tab.pivots',
          ),
          counter: this.widget?.dimensions_pivot?.length ?? 0,
        },
      ],
    };
  }

  private applyDatasourceFilter(): void {
    // * Apply filter to the Datasource picker to only show Feedst

    const specialChartTypeMap = new Map<number, number[]>([
      [NomChartType.BarCombined, [NomObjectTypes.COMBINED_FEED]], // Top Influencer Chart
      [NomChartType.ColumnCombined, [NomObjectTypes.COMBINED_FEED]], // Top Influencer Chart
    ]);

    if (specialChartTypeMap.has(this.widget.chart_type.id)) {
      const objectTypeIds = specialChartTypeMap.get(this.widget.chart_type.id);
      const formProperty = this.formProperties.find(
        (prop) => prop.propertyName === 'settings.datasource_object_id',
      );

      if (formProperty) {
        formProperty.additionalConfig = {
          ...formProperty.additionalConfig,
          objectTypeIds,
        };
      }
      return;
    }
  }
  private initializeColorsControl(): void {
    this.formGroup.addControl(
      'settings.datasource_color_id',
      new FormControl(this.widget.settings.datasource_color_id),
    );
    this.formGroup.addControl(
      'settings.dimension_pivot_color',
      new FormControl(this.widget.settings.dimension_pivot_color),
    );
  }

  formProperties: FormProperty[] = [];

  private initializeFormProperties(): void {
    this.formProperties = [
      {
        propertyName: 'language_picker',
        controlType: 'language-picker',
      },
      {
        propertyName: 'title',
        propertyNameLanguages: {
          [NomLanguages.DEFAULT]: 'title',
          [NomLanguages.BULGARIAN]:
            'settings.export_title.language_id_' + NomLanguages.BULGARIAN,
          [NomLanguages.ENGLISH]:
            'settings.export_title.language_id_' + NomLanguages.ENGLISH,
          [NomLanguages.ARABIC]:
            'settings.export_title.language_id_' + NomLanguages.ARABIC,
        },
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.widget-title',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.widget-title',
          ),
        },
        validators: [Validators.required],
      },
      {
        propertyName: 'widget_annotation',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.widget-anotation',
        ),
        controlType: 'textarea',
        propertyNameLanguages: {
          [NomLanguages.DEFAULT]: 'widget_annotation',
          [NomLanguages.BULGARIAN]:
            'settings.export_annotation.language_id_' + NomLanguages.BULGARIAN,
          [NomLanguages.ENGLISH]:
            'settings.export_annotation.language_id_' + NomLanguages.ENGLISH,
          [NomLanguages.ARABIC]:
            'settings.export_annotation.language_id_' + NomLanguages.ARABIC,
        },
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.widget-anotation',
          ),
          rows: 4,
        },
      },
      {
        propertyName: 'settings.icon_uri',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.icon-uri',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.icon-uri',
          ),
        },
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.widget_header_color',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.widget-header-color',
        ),
        controlType: 'colorpicker',
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.post_background_color',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.post-background-color',
        ),
        controlType: 'colorpicker',
        isMassUpdate: true,
      },
      {
        propertyName: 'tabs',
        controlType: 'tabs',
        additionalConfig: {
          formTab: this.formTabDataSources,
        },
        isMassUpdate: false,
      },
      {
        propertyName: 'settings.datasource_object_id',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.select-datasource',
          ),
          formGroupProperty: 'settings.datasource_object_id',
          formGroupClassIdProperty: 'settings.datasource_object_class_id',
          formGroupColorProperty: 'settings.datasource_color_id',
          objectTypeIds: [
            NomObjectTypes.ONLINE_FEED,
            NomObjectTypes.SOCIAL_FEED,
            NomObjectTypes.CLIPPING_FEED,
            NomObjectTypes.BROADCAST_FEED,
            NomObjectTypes.COMBINED_FEED,
          ],
          widget: this.widget,
        },
        controlType: 'ds-picker',
        tabIndex: 0,
        tabName: 'datasources',
        isMassUpdate: false,
      },
      {
        propertyName: 'settings.dimension_id_pivot',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.select-pivot-dimension',
          ),
          formGroupProperty: 'settings.dimension_id_pivot',
          formGroupColorProperty: 'settings.dimension_pivot_color',
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          widget: this.widget,
        },
        controlType: 'ds-picker',
        tabIndex: 0,
        tabName: 'pivots',
        isMassUpdate: false,
      },
      {
        propertyName: 'settings.dimension_id_filter',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.pivot-dimension-filter',
          ),
          formGroupProperty: 'settings.dimension_id_filter',

          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          widget: this.widget,
        },
        controlType: 'ds-picker',
        tabIndex: 0,
        tabName: 'filters',
        isMassUpdate: false,
      },

      {
        propertyName: 'settings.custom_period',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.custom-period',
        ),
        controlType: 'checkbox',
        defaultValue: false,
        formChanger: true,
        isMassUpdate: true,
        onChange: (value, formGroup) => {
          if (!value) {
            formGroup
              .get(['settings.feed.content_filters.time_start'])
              ?.setValue(null);
            formGroup
              .get(['settings.feed.content_filters.time_end'])
              ?.setValue(null);
          }
        },
      },
      {
        propertyNames: [
          'settings.feed.content_filters.time_start',
          'settings.feed.content_filters.time_end',
        ],
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.period',
        ),
        controlType: 'date-range-picker',
        additionalConfig: {
          presetBar: true,
          historyPeriods: false,
          timeDetails: true,
        },
        shouldDisplay: (formGroup) => {
          return formGroup.get(['settings.custom_period'])?.value === true;
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.number_of_items',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.limit-series-values-to-top',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'input-number',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.limit-series-values-to-top',
          ),
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.data_point_sort',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.data-point-sorting',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        valueOptions: this.formService.dataPointSortingOptions,
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.dataset_colors_source',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.dataset-colors-source',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        valueOptions: this.formService.datasetColorsSourceOptions,
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.feed.media_channel_id',
        // You can use a translation key or the hardcoded string 'Media Type'
        propertyLabel: this.translateService.instant(
          'widget-settings-general.label.media-channel',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: NomMediaChannel.ALL,

        onChange: (selectedId, formGroup) => {
          this.logger.debug(`Media Channel changed to ${selectedId}`);
        },
        formChanger: true,
        valueOptions: [
          {
            name: this.translateService.instant('media-channel.all'),
            value: NomMediaChannel.ALL,
          },
          {
            name: this.translateService.instant('media-channel.online'),
            value: NomMediaChannel.ONLINE,
          },
          {
            name: this.translateService.instant('media-channel.social'),
            value: NomMediaChannel.SOCIAL,
          },
          {
            name: this.translateService.instant('media-channel.print'),
            value: NomMediaChannel.PRINT,
          },
          {
            name: this.translateService.instant('media-channel.broadcast'),
            value: NomMediaChannel.BROADCAST,
          },
        ],
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.feed.content_filters.order',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.sort-by-metric',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: 'post_time_published',
        valueOptionsAsync: this.filterOptionsSubject.asObservable(),
        shouldDisplay: () => {
          return (
            this.massUpdate ||
            this.widget?.chart?.id == 6119 ||
            this.widget?.chart?.id == 6137
          ); // Clipping: Feed Content / Top Online Content (skip Top FB/TW widgets)
        },
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.feed.content_filters.sort',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.sort-direction',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: 'desc',
        valueOptions: [
          {
            name: this.translateService.instant('forms.form-option.descending'),
            value: 'desc',
          },
          {
            name: this.translateService.instant('forms.form-option.ascending'),
            value: 'asc',
          },
        ],
        shouldDisplay: () => {
          return (
            this.massUpdate ||
            this.widget?.chart?.id == 6119 ||
            this.widget?.chart?.id == 6137
          ); // Clipping: Feed Content / Top Online Content (skip Top FB/TW widgets)
        },
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.display_unique_sources',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.display-content-from-unique-sources',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'checkbox',
        shouldDisplay: (formGroup) => {
          return (
            this.massUpdate ||
            (formGroup.get(['settings.feed.media_channel_id']) as FormControl)
              ?.value != NomMediaChannel.SOCIAL
          );
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.display_unique_titles',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.display-content-with-unique-titles',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'checkbox',
        shouldDisplay: (formGroup) => {
          return (
            this.massUpdate ||
            (formGroup.get(['settings.feed.media_channel_id']) as FormControl)
              ?.value != NomMediaChannel.SOCIAL
          );
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.metrics',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.summary-properties',
        ),
        controlType: 'structured-checkbox-array',
        propertyGroup: {
          key: 'widget-settings-general.group.summary-properties',
          style: 'summary-properties',
        },
        additionalConfig: {
          valuesAsync: () => this.summaryFilterOptionsSubject.asObservable(),
        },
        shouldDisplay: () => {
          const isSummaryWidget =
            this.widget?.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY;

          const hasSummaryInMassUpdate =
            this.massUpdate &&
            this.widgets?.some(
              (w) => w?.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY,
            );

          const isSummaryContext = isSummaryWidget || hasSummaryInMassUpdate;

          const isAllowedChart = this.widget?.chart?.id !== 6215;

          return isSummaryContext && (this.massUpdate || isAllowedChart);
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.summary_enable_trends',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.enable-trends-comparison',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.summary-properties',
          style: 'summary-properties',
        },
        controlType: 'checkbox',
        shouldDisplay: () => {
          const isSummaryWidget =
            this.widget?.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY;

          const hasSummaryInMassUpdate =
            this.massUpdate &&
            this.widgets?.some(
              (w) => w?.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY,
            );

          const isSummaryContext = isSummaryWidget || hasSummaryInMassUpdate;

          const isAllowedChart = this.widget?.chart?.id !== 6215;

          return isSummaryContext && (this.massUpdate || isAllowedChart);
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.filters',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.display-fields',
        ),
        controlType: 'checkbox-array',
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        additionalConfig: {
          values: this.formService.topInfluencerFilters,
        },
        shouldDisplay: () => {
          return this.widget?.chart?.id == 6215; // Top Influencer Chart
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.activate_others',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.include-others-category',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'toggle',
        additionalConfig: {
          size: 'small',
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.include-others-category',
          ),
          tripleStateMode: this.massUpdate ? 'nullable' : 'none',
        },
        isMassUpdate: true,
      },

      {
        propertyName: 'settings.order_by_field.filter',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.sort-by-metric-decending',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: 'post_id_count',
        valueOptions: [
          {
            name: this.translateService.instant(
              'widget.settings-general.label.posts',
            ),
            value: 'post_id_count',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.potential-reach',
            ),
            value: 'post_social_followers_sum',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.potential-reach-per-post',
            ),
            value: 'post_social_followers_avg',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.reactions',
            ),
            value: 'post_social_reactions_sum',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.interactions',
            ),
            value: 'post_social_interactions_sum',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.engagements',
            ),
            value: 'post_social_engagements_sum',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.label.engagements-per-post',
            ),
            value: 'post_social_engagements_avg',
          },

          {
            name: this.translateService.instant(
              'widget.settings-general.label.engagement-ratio',
            ),
            value: 'post_social_engagement_ratio',
          },

          {
            name: this.translateService.instant(
              'widget.settings-general.label.interaction-ratio',
            ),
            value: 'post_social_interaction_ratio',
          },

          {
            name: this.translateService.instant(
              'widget.settings-general.label.total-statuses',
            ),
            value: 'post_social_author_statuses_count_avg',
          },
        ],
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.datasource_filter_by_first',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.filter-data-series-by-first',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: 'auto',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.filter-data-series-by-first',
          ),
        },
        valueOptions: [
          {
            name: this.translateService.instant(
              'widget.settings-general.option.auto',
            ),
            value: 'auto',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.option.yes',
            ),
            value: '1',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.option.no',
            ),
            value: '0',
          },
        ],
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.chart_stacking',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.chart-stacking-mode',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: 'normal',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.chart-stacking-mode',
          ),
        },
        valueOptions: [
          {
            name: this.translateService.instant(
              'widget.settings-general.option.normal',
            ),
            value: 'normal',
          },
          {
            name: this.translateService.instant(
              'widget.settings-general.option.stacked',
            ),
            value: 'stacked',
          },
        ],
        isMassUpdate: true,
      },
      {
        propertyName: 'settings.period_gap',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.data-aggregation-interval',
        ),
        propertyGroup: {
          key: 'widget-settings-general.group.data-series-settings',
          style: 'data-series-settings',
        },
        controlType: 'select',
        defaultValue: '+1DAY',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'widget.settings-general.placeholder.data-aggregation-interval',
          ),
        },
        valueOptions: [
          { name: '1 DAY', value: '+1DAY' },
          { name: '1 HOUR', value: '+1HOUR' },
          { name: '1 WEEK', value: '+1WEEK' },
          { name: '1 MONTH', value: '+1MONTH' },
          { name: '1 QUARTER', value: '+1QUARTER' },
          { name: '1 YEAR', value: '+1YEAR' },
        ],
        isMassUpdate: true,
      },
    ];
  }
}
